export const STEPUP_VERIFIER_REQUEST = 'WEBAUTHN_STEPUP_VERIFIER_REQUEST';
export const STEPUP_VERIFIER_RESPONSE = 'WEBAUTHN_STEPUP_VERIFIER_RESPONSE';

export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 10_000;
export const DEFAULT_HANDSHAKE_INTERVAL_MS = 500;

export interface StepUpVerifierResponse {
  codeVerifier: string;
  clientId: string;
  redirectUri: string;
}

export interface RequestStepUpVerifierOptions {
  /** UUID that correlates this request with the opener's reply. */
  exchangeId: string;
  /**
   * The `origin` URL param — the exact origin we postMessage the request to and
   * the only origin whose reply we accept. Must be a concrete origin (never '*').
   */
  expectedOrigin: string;
  timeoutMs?: number;
  intervalMs?: number;
}

/**
 * Asks the opener (account-settings) for the PKCE verifier that pairs with the
 * step-up authorization code. Re-sends the request on an interval until a valid,
 * matching reply arrives or the timeout elapses.
 *
 * Security: the reply is accepted only when `event.origin` strictly equals
 * `expectedOrigin`, its type is the expected response type, and its `exchangeId`
 * matches. The code and verifier are never logged.
 */
export const requestStepUpVerifier = ({
  exchangeId,
  expectedOrigin,
  timeoutMs = DEFAULT_HANDSHAKE_TIMEOUT_MS,
  intervalMs = DEFAULT_HANDSHAKE_INTERVAL_MS,
}: RequestStepUpVerifierOptions): Promise<StepUpVerifierResponse> => {
  return new Promise<StepUpVerifierResponse>((resolve, reject) => {
    const opener = window.opener as Window | null;
    if (!opener || opener.closed) {
      reject(
        new Error('No opener window available for step-up verifier handshake'),
      );
      return;
    }

    let settled = false;
    const teardownTasks: (() => void)[] = [];
    const cleanup = () => teardownTasks.forEach((task) => task());

    const onMessage = (event: MessageEvent) => {
      if (settled) {
        return;
      }
      // Strict origin check before touching the payload.
      if (event.origin !== expectedOrigin) {
        return;
      }
      const data = event.data as Partial<StepUpVerifierResponse> & {
        type?: string;
        exchangeId?: string;
      };
      if (
        !data ||
        data.type !== STEPUP_VERIFIER_RESPONSE ||
        data.exchangeId !== exchangeId
      ) {
        return;
      }
      const { codeVerifier, clientId, redirectUri } = data;
      // Ignore malformed replies and keep waiting/retrying until timeout.
      if (!codeVerifier || !clientId || !redirectUri) {
        return;
      }
      settled = true;
      cleanup();
      resolve({ codeVerifier, clientId, redirectUri });
    };

    const sendRequest = () => {
      try {
        opener.postMessage(
          { type: STEPUP_VERIFIER_REQUEST, exchangeId },
          expectedOrigin,
        );
      } catch {
        // Transient postMessage failure; the interval will retry.
      }
    };

    window.addEventListener('message', onMessage);
    teardownTasks.push(() => window.removeEventListener('message', onMessage));

    // Fire immediately, then keep re-sending — the opener answers every matching
    // request while the flow is active.
    sendRequest();
    const intervalId = setInterval(sendRequest, intervalMs);
    teardownTasks.push(() => clearInterval(intervalId));

    const timeoutId = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(
        new Error(
          'Timed out waiting for step-up verifier response from opener',
        ),
      );
    }, timeoutMs);
    teardownTasks.push(() => clearTimeout(timeoutId));
  });
};
